import React from 'react';
import { useParams } from 'react-router-dom';

import { EHeadingSize } from '@domain/enums/components/EHeading';
import { EProgressBarStep } from '@domain/enums/onboarding/progressBar/ESteps';
import { EButtonTextColor, EButtonVariant } from '@domain/enums/components/EButton';
import { EAdSenseProvider } from '@domain/enums/common/EAdSense';
import { ESynchronizationType } from '@domain/enums/hooks/ESynchronization';
import { IParams } from '@domain/interfaces/IParams';
import { IMarketingContentProps } from '@domain/interfaces/dashboard/IntegrationCenter/Marketing/IMarketingContent';
import { useFacebookCredentials } from '@helpers/hooks/common/integrations/marketing/facebook/useFacebookCredentials';
import { useSynchronization } from '@helpers/hooks/common/useSynchronization';
import { useConfig } from '@helpers/hooks/useConfig';
import { useOnboarding } from '@helpers/hooks/pages/onboarding/useOnboarding';
import { FacebookAdAccountsProvider } from '@helpers/hooks/common/integrations/marketing/facebook/useFacebookAdAccounts';

import FacebookImg from '@assets/pages/Onboarding/dropshipping/marketing/facebook.svg';

import FacebookOnboardingButton from '@components/common/core/Utils/FacebookOnboardingButton';
import SideBar from '@components/Onboarding/SideBar';
import NextStep from '@components/Onboarding/NextStep';
import PageError from '@components/common/core/Utils/PageError';
import Header from '../Header';
import AdAccountsTable from '../Table';

import { PageSkeletonLoading, TableSkeletonLoading } from '../SkeletonLoading';

import * as S from './styles';

const Wrapper: React.FC<IMarketingContentProps> = ({ callback }) => {
  const { analytics } = useConfig();
  const { isLoadingTrial } = useOnboarding();
  const { storeAliasId } = useParams<IParams>();
  const { currentSynchronizations } = useSynchronization();
  const {
    adSenseCredentials,
    isLoadingFacebookCredentialsData,
    isValidatingFacebookCredentialsData,
    isFacebookCredentialsDataError,
    isLoggingOut,
    isLoggingIn,
    mutate,
    handleFacebookData,
    handleFacebookProfileData,
  } = useFacebookCredentials();

  const handleFail = React.useCallback(() => {
    handleFacebookData(undefined);
    handleFacebookProfileData(undefined);
  }, [handleFacebookData, handleFacebookProfileData]);

  const handleNextStepButtonClick = React.useCallback(() => {
    callback();

    analytics?.track(
      'Onboarding Marketing Integration Done',
      {
        provider: EAdSenseProvider.FACEBOOK,
      },
      { context: { groupId: storeAliasId } },
    );
  }, [analytics, storeAliasId, callback]);

  const isSyncing = Boolean(
    currentSynchronizations?.synchronizations.find(synchronization => {
      if (
        synchronization.type === ESynchronizationType.AD_SENSE_ACCOUNTS ||
        synchronization.type === ESynchronizationType.AD_SENSE_CAMPAIGNS
      ) {
        return true;
      }

      return false;
    }),
  );

  const isLoading =
    isLoadingFacebookCredentialsData ||
    isValidatingFacebookCredentialsData ||
    isLoggingIn ||
    isLoggingOut;

  if (isLoading) {
    return (
      <S.Container>
        <SideBar
          onboardingStep={EProgressBarStep.INTEGRATIONS}
          linkToGoBack={`/onboarding/${storeAliasId}/dropshipping/integrations`}
        />

        <PageSkeletonLoading />
      </S.Container>
    );
  }

  if (isFacebookCredentialsDataError && !isValidatingFacebookCredentialsData) {
    return (
      <S.Container>
        <SideBar
          onboardingStep={EProgressBarStep.INTEGRATIONS}
          linkToGoBack={`/onboarding/${storeAliasId}/dropshipping/integrations`}
        />

        <S.ErrorWrapper>
          <PageError mutate={mutate} />
        </S.ErrorWrapper>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <SideBar
        onboardingStep={EProgressBarStep.INTEGRATIONS}
        linkToGoBack={`/onboarding/${storeAliasId}/dropshipping/integrations`}
      />

      {Boolean(adSenseCredentials.length) && (
        <S.HeaderAndTableContainer>
          <Header />

          {isSyncing && <TableSkeletonLoading />}

          {!isSyncing && (
            <>
              {adSenseCredentials.map((adSenseCredential, index) => (
                <FacebookAdAccountsProvider
                  adSenseCredentialAliasId={adSenseCredential.alias_id}
                  key={adSenseCredential.alias_id}
                >
                  <AdAccountsTable
                    openFirstProfile={Boolean(index === 0)}
                    adSenseCredential={adSenseCredential}
                  />
                </FacebookAdAccountsProvider>
              ))}
            </>
          )}
        </S.HeaderAndTableContainer>
      )}

      {Boolean(!adSenseCredentials.length) && (
        <S.ContentWrapper>
          <S.Image src={FacebookImg} alt="profitfy-goat-with-facebook-logo" />

          <S.Heading type={EHeadingSize.H2}>Conectar perfil do facebook</S.Heading>

          <S.ButtonsWrapper>
            <FacebookOnboardingButton
              width={379}
              onSuccess={handleFacebookData}
              onProfileSuccess={handleFacebookProfileData}
              onFail={handleFail}
            />

            <S.HorizontalRuleWrapper>
              <S.HorizontalRule />
              <S.HorizontalRuleText>Ou</S.HorizontalRuleText>
              <S.HorizontalRule />
            </S.HorizontalRuleWrapper>

            <S.SkipStepButton
              onClick={handleNextStepButtonClick}
              variant={EButtonVariant.SECONDARY_BUTTON}
            >
              Conectar depois
            </S.SkipStepButton>
          </S.ButtonsWrapper>
        </S.ContentWrapper>
      )}

      {Boolean(adSenseCredentials.length) && (
        <NextStep
          onClick={handleNextStepButtonClick}
          textColor={EButtonTextColor.DARK}
          disabled={isSyncing || isLoadingTrial}
          isLoading={isSyncing || isLoadingTrial}
        >
          Próximo Passo
        </NextStep>
      )}
    </S.Container>
  );
};

export default Wrapper;
